/**
 * studentQuestsViewModel — ce que « Mes quêtes » (`<StudentQuestsPage>`) lit dans le store.
 *
 * Patron A (adossé au store) : `getSkillSets` pose les quêtes sur `currentUser.object.skill_sets`
 * (réducteur `currentUser`), les compétences viennent de `staticData.skills`. L'écran relance la
 * lecture à l'ouverture et à la flèche, comme l'ancien (`MySkillSetsTabScreen.js` : au montage et au
 * tirer-pour-rafraîchir).
 *
 * Un échec ne bloque la page que s'il n'y a rien à montrer ; avec des quêtes déjà là, la flèche
 * s'arrête et la page reste (l'ancien écran le disait dans une boîte globale).
 */
import { createSelector } from "reselect"

import {
    toUIQuests,
    type ApiGameSkill,
    type ApiGameSkillSet,
    type ApiGameStatistics,
} from "../../../adapters/student/shared/questAdapter"
import { type UIQuest } from "../../../ui/types/studentGame"
import { selectDisplayedStudent } from "../../shared/displayedStudent"

export type StudentQuestsViewModel = {
    /** `null` tant que les quêtes ou les compétences ne sont pas là. */
    quests: UIQuest[] | null
    loading: boolean
    /** Bloquant : seulement quand il n'y a rien à montrer. */
    error: unknown | null
    refreshing: boolean
}

// Le store est en JS non typé : on lit les champs qu'on connaît, sans le décrire en entier.
/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
type ReduxState = any

type ApiSlice = { request?: boolean; error?: unknown }

const selectSkillSets = (state: ReduxState): ApiGameSkillSet[] | undefined => selectDisplayedStudent(state)?.skill_sets
const selectStatistics = (state: ReduxState): ApiGameStatistics | undefined => selectDisplayedStudent(state)?.statistics
const selectSkills = (state: ReduxState): ApiGameSkill[] | undefined => state?.data?.staticData?.skills
const selectSkillSetsReq = (state: ReduxState): ApiSlice | undefined => state?.api?.getSkillSets

export const studentQuestsViewModel = createSelector(
    [selectSkillSets, selectSkills, selectStatistics, selectSkillSetsReq],
    (skillSets, skills, statistics, req): StudentQuestsViewModel => {
        const quests = toUIQuests(skillSets, skills, statistics)
        const error = req?.error ?? null
        return {
            quests,
            loading: quests == null && error == null,
            error: quests == null ? error : null,
            refreshing: quests != null && !!req?.request,
        }
    }
)
